import type {FieldChange} from './ChangeEngine';

/**
 * Convert a single FieldChange into a human-readable description string.
 */
export function describeChange(change: FieldChange): string {
    const {displayName, changeType, prevDisplay, currDisplay, addedItems, removedItems} = change;

    switch (changeType) {
        case 'set':
            return `Set ${displayName} to ${currDisplay}`;
        case 'cleared':
            return `Cleared ${displayName}`;
        case 'enabled':
            return `Enabled ${displayName}`;
        case 'disabled':
            return `Disabled ${displayName}`;
        case 'changed':
            if (addedItems?.length && removedItems?.length) {
                const added = `Added ${addedItems.length}: ${addedItems.join(', ')}`;
                const removed = `Removed ${removedItems.length}: ${removedItems.join(', ')}`;
                return `Changed ${displayName} (${added}; ${removed})`;
            }
            if (prevDisplay && currDisplay) {
                return `Changed ${displayName} from ${prevDisplay} to ${currDisplay}`;
            }
            return `Changed ${displayName}`;
        case 'items_added':
            if (addedItems?.length === 1) {
                return `Added ${displayName}: ${addedItems[0]}`;
            }
            return `Added ${addedItems?.length || 0} ${displayName}: ${addedItems?.join(', ') || ''}`;
        case 'items_removed':
            if (removedItems?.length === 1) {
                return `Removed ${displayName}: ${removedItems[0]}`;
            }
            return `Removed ${removedItems?.length || 0} ${displayName}: ${removedItems?.join(', ') || ''}`;
        default:
            return `Updated ${displayName}`;
    }
}

/**
 * Convert an array of FieldChanges into human-readable description strings.
 */
export function describeChanges(changes: FieldChange[]): string[] {
    return changes.map(describeChange);
}

/**
 * Produce a single-line summary of changes for the Activity Stream.
 *
 * - 1 change: "JCM changed Status from Draft to Finalized"
 * - 2-3 changes: "JCM updated Status, Tasks, Due Date"
 * - 4+ changes: "JCM made 8 changes"
 */
export function summarizeChanges(changes: FieldChange[], actorInitials: string): string {
    if (changes.length === 0) return `${actorInitials} made no changes`;

    if (changes.length === 1) {
        const desc = describeChange(changes[0]);
        // Lowercase the first letter since we prepend actor
        return `${actorInitials} ${desc.charAt(0).toLowerCase()}${desc.slice(1)}`;
    }

    if (changes.length <= 3) {
        const names = changes.map(c => c.displayName);
        return `${actorInitials} updated ${names.join(', ')}`;
    }

    return `${actorInitials} made ${changes.length} changes`;
}
